/**
 * BroadcastSkillEffect — Specter veil-path active: false noise pulse.
 *
 * Instantly pulses a false sound event. All enemies within detection range
 * have their sound-source perception displaced: for the broadcast duration
 * they hear the player's sounds as originating from a point 5 tiles away in
 * a random direction. Sound-triggered aggro chases a lie.
 *
 * Upgrade hooks (pre-wired, values resolved at each activation):
 * - Resonant (durationMs +3000, displacementTiles +2): longer window, wider
 *   displacement. BroadcastSkillEffect reads both additively at activation.
 * - Feedback (freezeAggroWhileInvestigating): while Broadcast is active,
 *   enemies whose investigation target is the false source have aggroFrozen.
 *   BroadcastSkillEffect checks per-frame via ai.isInvestigatingFalseSource().
 * - Feedback sound degradation (soundDegradationPerSecond, soundDegradationMax):
 *   per-second permanent sound degradation for enemies affected by Broadcast,
 *   parallel to the Veil/Mirage accumulator. Resolved from Broadcast upgrades.
 *
 * Random direction for the false position is provided by deps.rng.getAngle().
 * False position = playerPos + (cos(angle), sin(angle)) × displacementPx.
 *
 * isActive() returns true during the active broadcast window. Cooldown starts
 * when the window expires (long tier). Fizzle at low XP (cooldown on fizzle).
 *
 * Implements SkillEffect directly — same DI architecture as OrchestrateSkillEffect.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Broadcast, Resonant, Feedback, White Noise
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BroadcastDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
    /** Current player position in pixels. */
    getPlayerPosition(): { x: number; y: number };
    /** All enemies within detection range of the player's current position. */
    getEnemiesInRange(): string[];
  };
  readonly rng: {
    /** Returns a random angle in [0, 2π) for the displacement direction. */
    getAngle(): number;
  };
  readonly ai: {
    /** Set a sound-source override on an enemy for the duration of Broadcast. */
    setSoundSourceOverride(enemyId: string, pos: { x: number; y: number }): void;
    /** Remove the sound-source override (called on expiry). */
    clearSoundSourceOverride(enemyId: string): void;
    /** Freeze/unfreeze aggro escalation on an enemy (Feedback hook). */
    setAggroFrozen(enemyId: string, frozen: boolean): void;
    /**
     * Returns true if this enemy is currently investigating Broadcast's
     * false source (used by the Feedback hook to freeze aggro).
     */
    isInvestigatingFalseSource(enemyId: string): boolean;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs: number;
      targetEntityId: string;
      perceptionReduction?: number;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BroadcastConfig {
  readonly cooldownMs: number;
  readonly baseDurationMs: number;
  readonly baseDisplacementTiles: number;
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: BroadcastConfig = {
  cooldownMs: 3000,           // long cooldown tier
  baseDurationMs: 6000,       // 6-second base broadcast window
  baseDisplacementTiles: 5,   // false source displaced 5 tiles from player
  tileSizePx: 32,
};

// ---------------------------------------------------------------------------
// Internal per-enemy White Noise tracking (mirrors VeilSkillEffect pattern)
// ---------------------------------------------------------------------------

interface WhiteNoiseTracker {
  accumulatorMs: number;
  appliedReduction: number;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BroadcastSkillEffect implements SkillEffect {
  readonly skillId = 'broadcast';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: BroadcastDeps;
  private readonly config: BroadcastConfig;

  private _cooldownRemaining = 0;
  private _durationRemaining = 0;

  // Activation state
  private _fizzled = false;
  private _activationCount = 0;
  private _affectedEnemyIds: string[] = [];
  private _falsePosition: { x: number; y: number } = { x: 0, y: 0 };

  // Resolved values (set at activation)
  private _resolvedDurationMs = 0;
  private _resolvedDisplacementTiles = 0;
  private _resolvedFreezeAggro = false;

  // White Noise trackers (pre-wired for task 150)
  private _whiteNoiseTrackers: Map<string, WhiteNoiseTracker> = new Map();

  // Feedback frozen enemy set (cleared on expiry)
  private _frozenEnemyIds: Set<string> = new Set();

  constructor(deps: BroadcastDeps, config: Partial<BroadcastConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }
  get affectedEnemyIds(): readonly string[] { return this._affectedEnemyIds; }
  get falsePosition(): Readonly<{ x: number; y: number }> { return this._falsePosition; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get resolvedDisplacementTiles(): number { return this._resolvedDisplacementTiles; }
  get resolvedFreezeAggro(): boolean { return this._resolvedFreezeAggro; }
  get durationRemaining(): number { return this._durationRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && this._durationRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Resolve upgrade effects at activation time
    this._resolvedDurationMs = this.config.baseDurationMs
      + this.deps.resolver.getAdditiveEffect('broadcast', 'durationMs');
    this._resolvedDisplacementTiles = this.config.baseDisplacementTiles
      + this.deps.resolver.getAdditiveEffect('broadcast', 'displacementTiles');
    this._resolvedFreezeAggro = this.deps.resolver.hasEffect('broadcast', 'freezeAggroWhileInvestigating');

    // Compute false position
    const playerPos = this.deps.targeting.getPlayerPosition();
    const angle = this.deps.rng.getAngle();
    const distancePx = this._resolvedDisplacementTiles * this.config.tileSizePx;
    this._falsePosition = {
      x: playerPos.x + Math.cos(angle) * distancePx,
      y: playerPos.y + Math.sin(angle) * distancePx,
    };

    // Apply sound-source override to all in-range enemies
    const enemies = this.deps.targeting.getEnemiesInRange();
    this._affectedEnemyIds = [...enemies];
    this._whiteNoiseTrackers.clear();
    this._frozenEnemyIds.clear();

    for (const enemyId of enemies) {
      this.deps.ai.setSoundSourceOverride(enemyId, this._falsePosition);
      this._whiteNoiseTrackers.set(enemyId, { accumulatorMs: 0, appliedReduction: 0 });
    }

    // Start duration timer — cooldown starts when window expires
    this._durationRemaining = this._resolvedDurationMs;
    this._activationCount++;

    return true;
  }

  deactivate(): void {
    // Instant skill — external deactivation not used
  }

  isActive(): boolean {
    return this._durationRemaining > 0;
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick broadcast window
    if (this._durationRemaining > 0) {
      this._durationRemaining = Math.max(0, this._durationRemaining - delta);

      // Per-frame Feedback hook: freeze aggro on investigating enemies
      if (this._resolvedFreezeAggro) {
        for (const enemyId of this._affectedEnemyIds) {
          const investigating = this.deps.ai.isInvestigatingFalseSource(enemyId);
          if (investigating && !this._frozenEnemyIds.has(enemyId)) {
            this.deps.ai.setAggroFrozen(enemyId, true);
            this._frozenEnemyIds.add(enemyId);
          } else if (!investigating && this._frozenEnemyIds.has(enemyId)) {
            this.deps.ai.setAggroFrozen(enemyId, false);
            this._frozenEnemyIds.delete(enemyId);
          }
        }
      }

      // White Noise accumulator (pre-wired for task 150)
      this._tickWhiteNoise(delta);

      // Broadcast expired this frame
      if (this._durationRemaining <= 0) {
        this._onExpiry();
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._durationRemaining = 0;
    this._whiteNoiseTrackers.clear();
    this._frozenEnemyIds.clear();
  }

  // --- Private ---

  private _onExpiry(): void {
    // Clear sound-source overrides from all affected enemies
    for (const enemyId of this._affectedEnemyIds) {
      this.deps.ai.clearSoundSourceOverride(enemyId);
    }

    // Clear aggro freeze (Feedback)
    for (const enemyId of this._frozenEnemyIds) {
      this.deps.ai.setAggroFrozen(enemyId, false);
    }
    this._frozenEnemyIds.clear();

    // Start cooldown after window expires (not at activation)
    this._cooldownRemaining = this.config.cooldownMs;
  }

  private _tickWhiteNoise(delta: number): void {
    const degradationPerSecond = this.deps.resolver.getAdditiveEffect(
      'broadcast', 'soundDegradationPerSecond',
    );
    if (degradationPerSecond <= 0) return; // Feedback sound degradation not unlocked

    const maxReduction = this.deps.resolver.getAdditiveEffect(
      'broadcast', 'soundDegradationMax',
    );

    for (const [enemyId, tracker] of this._whiteNoiseTrackers) {
      if (tracker.appliedReduction >= maxReduction) continue; // capped

      tracker.accumulatorMs += delta;
      const secondsElapsed = Math.floor(tracker.accumulatorMs / 1000);
      const prevSeconds = Math.floor((tracker.accumulatorMs - delta) / 1000);

      if (secondsElapsed > prevSeconds) {
        const ticksThisUpdate = secondsElapsed - prevSeconds;
        for (let i = 0; i < ticksThisUpdate; i++) {
          if (tracker.appliedReduction >= maxReduction) break;
          const increment = Math.min(degradationPerSecond, maxReduction - tracker.appliedReduction);
          tracker.appliedReduction += increment;
          // STUB: deferred — no enemy hearing system reads white noise debuff
          this.deps.statusEffects.apply('white_noise_debuff', {
            sourceSkillId: 'broadcast',
            durationMs: -1, // permanent
            targetEntityId: enemyId,
            perceptionReduction: increment,
          });
        }
      }
    }
  }
}
